import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosPublic, axiosPrivate } from "../utils";
import { toast } from 'react-toastify';

const modulePrefix = "auth";

const initialState = {
  user: JSON.parse(localStorage?.getItem("user")) || null,
  isAuthenticated: false,
  loading: false,
  reqLoading: false,
  success: false,
  error: false,
};

export const register = createAsyncThunk(
  `${modulePrefix}/register`,
  async (data, { rejectWithValue }) => {
    try {
      const res = await axiosPublic.post(`${modulePrefix}/register`, {
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
      });

      if (!res.data.result) {
        if (res.data.message) {
          toast.error(res.data.message);
        }
        return rejectWithValue(res.data.message);
      }

      return res.data.result;
    } catch (error) {
      console.log(error);
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      }
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const login = createAsyncThunk(
  `${modulePrefix}/login`,
  async (data, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.post(`${modulePrefix}/login`, {
        email: data.email,
        password: data.password,
      });

      if (!res.data.result) {
        if (res.data.message) {
          toast.error(res.data.message);
        }
        return rejectWithValue(res.data.message);
      }

      return res.data.result;
    } catch (error) {
      console.log(error);
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      }
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const checkToken = createAsyncThunk(
  `${modulePrefix}/check`,
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const res = await axiosPrivate.get(`${modulePrefix}/check`);

      if (!res.data.result) {
        return rejectWithValue(res.data.message);
      }

      return res.data.result;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const refreshToken = createAsyncThunk(
  `${modulePrefix}/refresh-token`,
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const res = await axiosPublic.post(`${modulePrefix}/refresh-token`, {
        token: state.userData.user?.refreshToken,
      });

      const newUser = {
        ...state.userData.user,
        token: res.data.token,
        refreshToken: res.data.refreshToken,
      };

      return newUser;
      if (!res.data.result) {
        return rejectWithValue(res.data.message);
      }

      return res.data.result;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const forgetPassword = createAsyncThunk(
  `${modulePrefix}/forget-password`,
  async (data, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.post(`${modulePrefix}/forget-password`, {
        email: data.email,
      });

      if (!res.data.result) {
        if (res.data.message) {
          toast.error(res.data.message);
        }
        return rejectWithValue(res.data.message);
      }

      toast.success('Password reset link send to email.');

      return res.data.result;
    } catch (error) {
      console.log(error);
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      }
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const resetPassword = createAsyncThunk(
  `${modulePrefix}/reset-password`,
  async (data, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.post(`${modulePrefix}/reset-password`, {
        newPassword: data.password,
        confirmNewPassword: data.confirmPassword,
        resetCode: data.resetCode,
      });

      if (!res.data.result) {
        if (res.data.message) {
          toast.error(res.data.message);
        }
        return rejectWithValue(res.data.message);
      }

      toast.success('Password changed successfull.');
      return res.data.result;
    } catch (error) {
      console.error(error);
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      }
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const resendEmailVerification = createAsyncThunk(
  `${modulePrefix}/resend-verification`,
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const res = await axiosPrivate.post(`${modulePrefix}/resend-verification`);

      if (!res.data.result) {
        if (res.data.message) {
          toast.error(res.data.message);
        }
        return rejectWithValue(res.data.message);
      }

      toast.success('Email verification link send to email.');
      return res.data.result;
    } catch (error) {
      console.error(error);
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      }
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    logoutUser(state, action) {
      localStorage.removeItem("user");
      state.user = null;
      state.username = "";
      state.password = "";
      state.isAuthenticated = false;
      state.success = false;
      state.error = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(register.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(register.fulfilled, (state, action) => {
        localStorage.setItem("user", JSON.stringify(action.payload));
        state.user = action.payload;
        state.loading = false;
        state.isAuthenticated = true;
      })
      .addCase(register.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(login.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        localStorage.setItem("user", JSON.stringify(action.payload));
        state.user = action.payload;
        state.loading = false;
        state.isAuthenticated = true;
      })
      .addCase(login.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        localStorage.setItem("user", JSON.stringify(action.payload));
        state.user = action.payload;
        state.loading = false;
      })
      .addCase(checkToken.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(checkToken.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = { ...state.user, ...action.payload };
        localStorage.setItem("user", JSON.stringify(state.user));
      })
      .addCase(checkToken.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
      })
      .addCase(forgetPassword.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(forgetPassword.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(forgetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(resetPassword.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(resendEmailVerification.pending, (state, action) => {
        state.reqLoading = true;
      })
      .addCase(resendEmailVerification.fulfilled, (state, action) => {
        state.reqLoading = false;
      })
      .addCase(resendEmailVerification.rejected, (state, action) => {
        state.reqLoading = false;
        state.error = action.payload;
      });
  },
});

export const { logoutUser } = userSlice.actions;
